// src/components/custom-features/image-picker/ImagePicker.tsx
import * as React from "react";
import { Dialog, DialogContent, DialogBody } from "components/ui/dialog";
import { cn } from "lib/utils";
import { getDatesForTheImageRes, getImagesByDateRes, getPreImgUrlRes, uploadImageToS3Res } from "core";
import { Input } from "components/ui/input";
import { Upload, Calendar, ImageIcon, Check, ChevronRight, ChevronLeft, Settings2, Link as LinkIcon, Tag, Maximize2, AlignJustify, Loader2 } from "lucide-react";

type ImagePropsMap = Partial<{ width: string; height: string; alt: string; link: string; id: string; class: string }>;

function ensureHttps(src: string) {
  if (!src) return src;
  return /^https?:\/\//i.test(src) ? src : `https://${src}`;
}

export type ImagePickerProps = {
  activeTab?: "existing" | "new" | "upload";
  handleClose: () => void;
  setImageToEditor: (selectedImage: string, imgProps: Record<string, string>) => void;
  showModal: boolean;
};

const ImagePicker: React.FC<ImagePickerProps> = ({ handleClose, setImageToEditor, showModal }) => {
  const [imageUploadedDate, setImageUploadedDate] = React.useState<string[]>([]);
  const [uploadedImages, setUploadedImages] = React.useState<string[]>([]);
  const [selectedImage, setSelectedImage] = React.useState<string>("");
  const [activeImageDate, setActiveImageDate] = React.useState<string>("");
  const [showProps, setShowProps] = React.useState(false);
  const [imgProperties, setImgProperties] = React.useState<ImagePropsMap>({});
  const [isLoading, setIsLoading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (!showModal) return;
    (async () => {
      setIsLoading(true);
      try {
        setSelectedImage(""); setImgProperties({}); setShowProps(false);
        const res = await getDatesForTheImageRes();
        const dates = Array.isArray(res?.data) ? res.data : [];
        setImageUploadedDate(dates);
        const latest = dates[dates.length - 1] ?? "";
        if (latest) {
          setActiveImageDate(latest);
          const imgs = await getImagesByDateRes(latest.replaceAll("/", "-"));
          setUploadedImages(Array.isArray(imgs?.data) ? imgs.data : []);
        } else { setActiveImageDate(""); setUploadedImages([]); }
      } finally { setIsLoading(false); }
    })();
  }, [showModal]);

  const onPickFile = () => fileInputRef.current?.click();

  const onFilePicked: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsLoading(true);
    try {
      const extension = (file.name && file.name.split(".").pop()) || "png";
      const pre = await getPreImgUrlRes(extension);
      const preAss = pre?.data;
      const fd = new FormData();
      fd.append("Content-Type", preAss.fields["Content-Type"]);
      fd.append("key", preAss.fields["key"]);
      fd.append("AWSAccessKeyId", preAss.fields["AWSAccessKeyId"]);
      fd.append("x-amz-security-token", preAss.fields["x-amz-security-token"]);
      fd.append("policy", preAss.fields["policy"]);
      fd.append("signature", preAss.fields["signature"]);
      fd.append("file", file);
      await uploadImageToS3Res(preAss.url, fd);
      const datesRes = await getDatesForTheImageRes();
      const dates = Array.isArray(datesRes?.data) ? datesRes.data : [];
      setImageUploadedDate(dates);
      const latest = dates[dates.length - 1] ?? "";
      setActiveImageDate(latest);
      if (latest) {
        const imgs = await getImagesByDateRes(latest.replaceAll("/", "-"));
        setUploadedImages(Array.isArray(imgs?.data) ? imgs.data : []);
        setSelectedImage(""); setShowProps(false);
      }
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const getImagesByDate = async (date: string) => {
    setActiveImageDate(date); setIsLoading(true);
    try {
      const imgs = await getImagesByDateRes(date.replaceAll("/", "-"));
      setUploadedImages(Array.isArray(imgs?.data) ? imgs.data : []);
      setSelectedImage(""); setShowProps(false);
    } finally { setIsLoading(false); }
  };

  const toggleSelect = (image: string) => {
    setShowProps(false);
    setSelectedImage((curr) => (curr === image ? "" : image));
  };

  const onSubmit = () => {
    if (!selectedImage) return;
    setImageToEditor(selectedImage, imgProperties as Record<string, string>);
    handleClose();
  };

  const propFields: { key: keyof ImagePropsMap; label: string; placeholder: string; icon: any; type?: string }[] = [
    { key: "width", label: "Width", placeholder: "e.g. 600", icon: Maximize2, type: "number" },
    { key: "height", label: "Height", placeholder: "e.g. 400", icon: Maximize2, type: "number" },
    { key: "alt", label: "Alt Text", placeholder: "Describe the image…", icon: AlignJustify },
    { key: "link", label: "Link URL", placeholder: "https://…", icon: LinkIcon },
    { key: "id", label: "Element ID", placeholder: "img-hero", icon: Tag },
    { key: "class", label: "CSS Classes", placeholder: "rounded-lg shadow-md", icon: Settings2 },
  ];

  return (
    <Dialog open={showModal} onOpenChange={(o) => (!o ? handleClose() : null)}>
      <DialogContent aria-labelledby="image-picker-title" size="lg" align="center" className="select-none dark:bg-dark-800 p-0 overflow-hidden">

        {/* ── Gradient Header ── */}
        <div className="relative overflow-hidden flex-shrink-0">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(99,102,241,0.4),transparent_60%)]" />
          <div className="relative px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20 flex-shrink-0">
              <ImageIcon className="w-4.5 h-4.5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 id="image-picker-title" className="text-title-sm font-bold text-white leading-tight">
                {showProps ? "Image Properties" : "Select Image"}
              </h2>
              <p className="text-[11px] text-white/55 mt-0.5">
                {showProps ? "Configure how the image appears in your template" : "Choose from your uploaded images or upload a new one"}
              </p>
            </div>
            {/* Step indicator */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <div className={cn("w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 transition-all",
                !showProps ? "bg-surface text-indigo-600 dark:text-indigo-300 border-white" : "bg-white/20 text-white/70 border-white/30")}>1</div>
              <ChevronRight className="w-3 h-3 text-white/40" />
              <div className={cn("w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 transition-all",
                showProps ? "bg-surface text-indigo-600 dark:text-indigo-300 border-white" : "bg-white/20 text-white/70 border-white/30")}>2</div>
            </div>
          </div>
        </div>

        <DialogBody className="p-5">
          {/* ── STEP 1: GALLERY ── */}
          {!showProps && (
            <div className="space-y-4">
              {/* Upload + Date filters row */}
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={onPickFile}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 h-9 px-4 rounded-xl text-[12px] font-bold
                    text-white bg-gradient-to-r from-blue-600 to-indigo-600
                    hover:from-blue-700 hover:to-indigo-700
                    shadow-sm shadow-blue-500/25
                    disabled:opacity-60 disabled:cursor-not-allowed
                    transition-all active:scale-[0.97]"
                >
                  {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  Upload Image
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onFilePicked} />

                {/* Date chips */}
                {imageUploadedDate.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap flex-1">
                    <Calendar className="w-3.5 h-3.5 text-content-subtle flex-shrink-0" />
                    {imageUploadedDate.map((d) => {
                      const active = activeImageDate === d;
                      return (
                        <button
                          key={d}
                          type="button"
                          onClick={() => getImagesByDate(d)}
                          disabled={isLoading}
                          className={cn(
                            "h-7 px-2.5 rounded-lg text-[11px] font-semibold transition-all",
                            active
                              ? "bg-indigo-600 text-white shadow-sm"
                              : "bg-gray-100 dark:bg-dark-700 text-content-secondary dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600"
                          )}
                        >{d}</button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Image Grid */}
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-4"
                style={{ maxHeight: 320, overflowY: "auto", scrollbarWidth: "thin" }}>
                {isLoading && uploadedImages.length === 0
                  ? Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="h-28 rounded-xl bg-gray-100 dark:bg-dark-700 animate-pulse" />
                  ))
                  : uploadedImages.map((image) => {
                    const full = ensureHttps(image);
                    const selected = selectedImage === image;
                    return (
                      <button
                        type="button"
                        key={image}
                        onClick={() => toggleSelect(image)}
                        disabled={isLoading}
                        aria-pressed={selected}
                        className={cn(
                          "group relative overflow-hidden rounded-xl border-2 transition-all duration-200",
                          "focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2",
                          selected
                            ? "border-indigo-500 shadow-md shadow-indigo-500/20"
                            : "border-transparent hover:border-indigo-300 dark:hover:border-indigo-600"
                        )}
                      >
                        <img
                          src={full}
                          alt=""
                          className={cn("block h-28 w-full object-cover transition-all", selected ? "brightness-90 scale-105" : "group-hover:scale-105")}
                          loading="lazy"
                        />
                        {/* Hover overlay */}
                        <div className={cn(
                          "absolute inset-0 flex items-center justify-center transition-all duration-200",
                          selected ? "bg-indigo-500/20" : "bg-transparent group-hover:bg-black/20"
                        )}>
                          {selected && (
                            <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center shadow-lg">
                              <Check className="w-4 h-4 text-white" strokeWidth={3} />
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })
                }
              </div>

              {!isLoading && !uploadedImages.length && (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <div className="w-12 h-12 rounded-2xl bg-gray-100 dark:bg-dark-700 flex items-center justify-center mb-3">
                    <ImageIcon className="w-6 h-6 text-content-subtle" />
                  </div>
                  <p className="text-[13px] font-semibold text-content-secondary dark:text-gray-300">
                    {activeImageDate ? "No images for this date" : "No images yet"}
                  </p>
                  <p className="text-[11px] text-content-subtle mt-1">Upload an image to get started</p>
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between pt-2 border-t border-line-subtle dark:border-dark-700">
                {selectedImage && (
                  <p className="text-[11px] text-indigo-600 dark:text-indigo-400 font-semibold flex items-center gap-1">
                    <Check className="w-3 h-3" /> 1 image selected
                  </p>
                )}
                <div className="ml-auto">
                  <button
                    type="button"
                    onClick={() => setShowProps(true)}
                    disabled={!selectedImage || isLoading}
                    className="inline-flex items-center gap-2 h-9 px-4 rounded-xl text-[12px] font-bold
                      text-white bg-gradient-to-r from-blue-600 to-indigo-600
                      hover:from-blue-700 hover:to-indigo-700
                      shadow-sm transition-all active:scale-[0.97]
                      disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 2: PROPERTIES ── */}
          {showProps && selectedImage && (
            <div className="space-y-5">
              {/* Preview of selected image */}
              <div className="relative rounded-xl overflow-hidden border border-line dark:border-dark-600 bg-gray-50 dark:bg-dark-900/30 h-28">
                <img src={ensureHttps(selectedImage)} alt="Selected" className="w-full h-full object-contain p-2" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
              </div>

              {/* Property fields */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {propFields.map(({ key, label, placeholder, icon: Icon, type }) => (
                  <div key={key}>
                    <label className="flex items-center gap-1.5 text-[11px] font-bold text-content-muted dark:text-gray-400 uppercase tracking-wide mb-1.5">
                      <Icon className="w-3 h-3" /> {label}
                    </label>
                    <Input
                      type={type || "text"}
                      placeholder={placeholder}
                      value={(imgProperties as any)[key] ?? ""}
                      onChange={(e) => setImgProperties((p) => ({ ...p, [key]: e.target.value }))}
                      className="h-9 rounded-xl text-[12px] dark:bg-dark-700 border-line dark:border-dark-600"
                    />
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-2 border-t border-line-subtle dark:border-dark-700">
                <button
                  type="button"
                  onClick={() => setShowProps(false)}
                  className="inline-flex items-center gap-2 h-9 px-4 rounded-xl text-[12px] font-semibold
                    text-content-secondary dark:text-gray-300
                    border border-line dark:border-dark-600
                    hover:bg-gray-100 dark:hover:bg-dark-700
                    transition-all"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Back
                </button>
                <button
                  type="button"
                  onClick={onSubmit}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 h-9 px-5 rounded-xl text-[12px] font-bold
                    text-white bg-gradient-to-r from-blue-600 to-indigo-600
                    hover:from-blue-700 hover:to-indigo-700
                    shadow-sm shadow-blue-500/25
                    disabled:opacity-60 disabled:cursor-not-allowed
                    transition-all active:scale-[0.97]"
                >
                  {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Insert Image
                </button>
              </div>
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default ImagePicker;
